/**
 * Currency Context
 * Manages currency preferences and exchange rate conversion
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from '../lib/axios'; // Use configured axios with CSRF
import { usePersistedState, STORAGE_KEYS } from '../hooks/usePersistedState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = usePersistedState(STORAGE_KEYS.CURRENCY, 'XCG');
  
  // Exchange rate state - fetched from API, fallback to defaults
  const [exchangeRate, setExchangeRate] = useState({
    USD_TO_XCG: 1.79,  // Fallback value
    XCG_TO_USD: 1 / 1.79,
    lastUpdated: null,
    source: 'fallback'
  });

  // Fetch exchange rate from backend on mount and every 30 minutes
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await axios.get(`${API}/currency/rate`);
        if (response.data && response.data.xcg_to_usd) {
          const xcgToUsd = response.data.xcg_to_usd;
          setExchangeRate({
            USD_TO_XCG: 1 / xcgToUsd,
            XCG_TO_USD: xcgToUsd,
            lastUpdated: response.data.last_updated,
            source: response.data.source || 'api'
          });
        }
      } catch (error) {
        console.warn('Failed to fetch exchange rate, using fallback:', error.message);
      }
    };

    // Fetch immediately on mount
    fetchExchangeRate();

    // Refresh every 30 minutes
    const interval = setInterval(fetchExchangeRate, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const { USD_TO_XCG, XCG_TO_USD } = exchangeRate;

  // Format number with European style (1.234,56)
  const formatEuropean = (num) => {
    return num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Format price showing both currencies
  const formatPrice = (amount, fromCurrency = 'USD') => {
    let usdAmount = amount;
    let xcgAmount = amount;

    // Convert to both currencies
    if (fromCurrency === 'USD') {
      xcgAmount = amount * USD_TO_XCG;
      usdAmount = amount;
    } else if (fromCurrency === 'XCG') {
      usdAmount = amount * XCG_TO_USD;
      xcgAmount = amount;
    }

    // Always show both currencies for clarity
    if (currency === 'both') {
      return `$ ${formatEuropean(usdAmount)} / XCG ${formatEuropean(xcgAmount)}`;
    }

    // Single currency display
    if (currency === 'USD') {
      return `$ ${formatEuropean(usdAmount)}`;
    } else {
      return `XCG ${formatEuropean(xcgAmount)}`;
    }
  };

  // Format price with both always shown (for listings)
  const formatDualPrice = (amount, fromCurrency = 'USD') => {
    let usdAmount = fromCurrency === 'USD' ? amount : amount * XCG_TO_USD;
    let xcgAmount = fromCurrency === 'XCG' ? amount : amount * USD_TO_XCG;
    
    return {
      usd: `$ ${formatEuropean(usdAmount)}`,
      xcg: `XCG ${formatEuropean(xcgAmount)}`,
      combined: `$ ${formatEuropean(usdAmount)} / XCG ${formatEuropean(xcgAmount)}`
    };
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      formatPrice, 
      formatDualPrice, 
      USD_TO_XCG, 
      XCG_TO_USD,
      exchangeRateSource: exchangeRate.source,
      exchangeRateUpdated: exchangeRate.lastUpdated
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
